import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { RotateCcw } from "lucide-react";
import {
  computeLinearRegression,
  computePolynomialRegression,
} from "../utils/computeRegression";
import { generateRandomColor } from "../functions/TrendLineChart";

ChartJS.register(CategoryScale, LinearScale, TimeScale, PointElement, LineElement, LineController, Title, Tooltip, Legend);

// ── Isolated chart component ──────────────────────────────────────────────────
// Each instance owns a fresh canvas DOM element. The parent changes the `key`
// prop when dataset count changes — React fully unmounts+remounts, giving
// Chart.js a completely clean canvas with no residual context state.
function ChartCanvas({ chartData, options, plugins, onInstance }) {
  const canvasRef    = useRef(null);
  const chartRef     = useRef(null);
  const skipFirstRef = useRef(true); // skip the [chartData] effect on initial mount

  // Create on mount, destroy on unmount.
  useEffect(() => {
    if (!canvasRef.current) return;
    skipFirstRef.current = true;
    const inst = new ChartJS(canvasRef.current, { type: "line", data: chartData, options, plugins });
    chartRef.current = inst;
    onInstance?.(inst);
    return () => { inst.destroy(); chartRef.current = null; onInstance?.(null); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // In-place data update — safe because key guarantees dataset count AND multiSelect state are stable.
  useEffect(() => {
    if (skipFirstRef.current) { skipFirstRef.current = false; return; }
    const chart = chartRef.current;
    if (!chart) return;
    if (chart.data.datasets.length !== chartData.datasets.length) return; // safety guard
    chart.data.datasets = chartData.datasets;
    if (chartData.labels !== undefined) chart.data.labels = chartData.labels;
    chart.update("none");
  }, [chartData]); // eslint-disable-line react-hooks/exhaustive-deps

  return <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />;
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  text:   "#1e1d3a",
  muted:  "#6b6a8f",
  subtle: "#9898b8",
  bg:     "#f6f6fb",
  border: "#e3e2f0",
  accent: "#232231",
  rSm:    6,
};

function DualRangeSlider({ label, unit = "", trackMin, trackMax, valueMin, valueMax, onChange, fmt }) {
  const trackRef = useRef(null);
  const dragRef  = useRef(null);
  const span = (trackMax - trackMin) || 1;
  const curMin = valueMin !== null ? valueMin : trackMin;
  const curMax = valueMax !== null ? valueMax : trackMax;
  const pct = v => Math.max(0, Math.min(100, (v - trackMin) / span * 100));
  const fmtVal = fmt || (v => Number(v).toFixed(1));

  const startDrag = (which) => (e) => {
    e.preventDefault();
    dragRef.current = which;
    const onMove = (ev) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return;
      const p = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
      const v = trackMin + p * span;
      if (dragRef.current === "min") onChange(Math.min(v, curMax - span * 0.004), curMax);
      else                           onChange(curMin, Math.max(v, curMin + span * 0.004));
    };
    const onUp = () => {
      dragRef.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup",   onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup",   onUp);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, color: T.muted }}>
        <span style={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</span>
        <span>{fmtVal(curMin)}{unit} – {fmtVal(curMax)}{unit}</span>
      </div>
      <div ref={trackRef} style={{ position: "relative", height: 6, background: T.border, borderRadius: 3, margin: "0 6px", userSelect: "none" }}>
        <div style={{ position: "absolute", left: `${pct(curMin)}%`, right: `${100 - pct(curMax)}%`, top: 0, bottom: 0, background: T.accent, borderRadius: 3, opacity: 0.35 }} />
        <div onMouseDown={startDrag("min")} style={{ position: "absolute", left: `${pct(curMin)}%`, top: "50%", transform: "translate(-50%,-50%)", width: 12, height: 12, borderRadius: "50%", background: "#fff", border: `2px solid ${T.accent}`, cursor: "ew-resize", zIndex: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }} />
        <div onMouseDown={startDrag("max")} style={{ position: "absolute", left: `${pct(curMax)}%`, top: "50%", transform: "translate(-50%,-50%)", width: 12, height: 12, borderRadius: "50%", background: T.accent, border: `2px solid ${T.accent}`, cursor: "ew-resize", zIndex: 2, boxShadow: "0 1px 4px rgba(0,0,0,0.2)" }} />
      </div>
    </div>
  );
}

function SectionLabel({ children, first }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: "0.07em",
      textTransform: "uppercase", color: T.muted,
      marginBottom: 8,
      marginTop: first ? 0 : 16,
    }}>
      {children}
    </div>
  );
}

function SectionBlock({ children, first }) {
  return (
    <div style={first ? {} : { borderTop: `1px solid ${T.border}`, paddingTop: 14, marginTop: 14 }}>
      {children}
    </div>
  );
}

function formatAlert(alert) {
  const v = String(alert ?? "").toLowerCase();
  if (v === "yes") return { text: "Alert", color: "red" };
  if (v === "no") return { text: "No Alert", color: "green" };
  return { text: "-", color: undefined };
}

function formatLabelValue(value) {
  if (value == null) return "";
  return String(value).replace(/_/g, " ").trim().replace(/^\w/, c => c.toUpperCase());
}

function DataRow({ label, value, title }) {
  return (
    <div style={{
      display: "grid", gridTemplateColumns: "1fr 1fr",
      fontSize: 12, color: T.text, padding: "4px 0",
      borderBottom: `1px solid ${T.border}`,
      textAlign: "left",
    }}>
      <span style={{ color: T.muted, cursor: title ? "help" : "default", borderBottom: title ? "1px dotted #9ca3af" : "none", width: "fit-content" }} title={title}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}

export default function TrendLine({ selectedPoints, multiSelect, setMultiSelect, allowedTools = [], selectedEpoch = null, onResetEpoch }) {
  const [showTrendLine, setShowTrendLine] = useState(false);
  const [showPolynomial, setShowPolynomial] = useState(false);
  const [polynomialDegree, setPolynomialDegree] = useState(2);
  const [axesSwapped,       setAxesSwapped]       = useState(false);
  const [showRangeControls, setShowRangeControls] = useState(false);
  const [defMin,  setDefMin]  = useState(null); // number | null
  const [defMax,  setDefMax]  = useState(null);
  const [dateMin, setDateMin] = useState(null); // timestamp | null
  const [dateMax, setDateMax] = useState(null);
  const colorCache       = useRef({});
  const chartInstanceRef = useRef(null);
  const epochTsRef       = useRef(null);
  const axesSwappedRef   = useRef(false);
  const onChartInstance  = useCallback((inst) => { chartInstanceRef.current = inst; }, []);

  useEffect(() => { axesSwappedRef.current = axesSwapped; }, [axesSwapped]);

  // Imperatively apply axis min/max without remounting the chart
  useEffect(() => {
    const chart = chartInstanceRef.current;
    if (!chart?.options?.scales) return;
    const { x, y } = chart.options.scales;
    if (axesSwapped) {
      if (x) { x.min = defMin  ?? undefined; x.max = defMax  ?? undefined; }
      if (y) { y.min = dateMin ?? undefined; y.max = dateMax ?? undefined; }
    } else {
      if (x) { x.min = dateMin ?? undefined; x.max = dateMax ?? undefined; }
      if (y) { y.min = defMin  ?? undefined; y.max = defMax  ?? undefined; }
    }
    chart.update("none");
  }, [defMin, defMax, dateMin, dateMax, axesSwapped]);

  useEffect(() => {
    if (!selectedEpoch) { epochTsRef.current = null; }
    else {
      const m = selectedEpoch.match(/^D_(\d{4})(\d{2})(\d{2})$/i);
      epochTsRef.current = m ? new Date(`${m[1]}-${m[2]}-${m[3]}`).getTime() : null;
    }
    chartInstanceRef.current?.update();
  }, [selectedEpoch]);

  const epochMarkerPlugin = useMemo(() => ({
    id: "epochMarker",
    afterDraw(chart) {
      const ts = epochTsRef.current;
      if (ts == null) return;
      const { ctx, scales: { x, y } } = chart;
      if (!x || !y) return;
      ctx.save();
      ctx.strokeStyle = "rgba(30, 120, 255, 0.9)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 3]);
      ctx.beginPath();
      if (axesSwappedRef.current) {
        const yPixel = y.getPixelForValue(ts);
        if (yPixel >= y.top && yPixel <= y.bottom) {
          ctx.moveTo(x.left, yPixel);
          ctx.lineTo(x.right, yPixel);
        }
      } else {
        const xPixel = x.getPixelForValue(ts);
        if (xPixel >= x.left && xPixel <= x.right) {
          ctx.moveTo(xPixel, y.top);
          ctx.lineTo(xPixel, y.bottom);
        }
      }
      ctx.stroke();
      ctx.restore();
    },
  }), []);

  const lastPoint = selectedPoints.length ? selectedPoints[selectedPoints.length - 1] : null;
  const blurred   = !lastPoint;

  const chartPoints = useMemo(() => {
    if (!lastPoint) return [];
    return multiSelect ? selectedPoints : [lastPoint];
  }, [selectedPoints, lastPoint, multiSelect]);

  const datasets = useMemo(() => {
    if (!chartPoints.length) return [];
    return chartPoints.map((pt, index) => {
      const key = `${pt.lat},${pt.lon}`;
      if (!colorCache.current[key]) colorCache.current[key] = generateRandomColor();
      const color = multiSelect ? colorCache.current[key] : "#232231";
      return {
        label: multiSelect ? `P${index + 1}` : "",
        data: pt.time.map((t, i) => axesSwapped
          ? { x: pt.displacement[i], y: new Date(t).getTime() }
          : { x: new Date(t).getTime(), y: pt.displacement[i] }
        ),
        borderColor: color,
        backgroundColor: color,
        pointRadius: 2,
        pointHoverRadius: blurred ? 0 : 3,
        fill: false,
        showLine: false,
      };
    });
  }, [chartPoints, multiSelect, blurred, axesSwapped]);

  const x = lastPoint?.time?.map((_, i) => i) || [];
  const y = lastPoint?.displacement || [];

  const trendInfo = useMemo(() => {
    if (!multiSelect && showTrendLine && x.length >= 2) {
      const { slope, intercept, r2, trendData } = computeLinearRegression(x, y);
      return { type: "linear", slope, intercept, r2, trendData };
    }
    if (!multiSelect && showPolynomial && x.length >= 2) {
      const { equation, r2, trendData } = computePolynomialRegression(x, y, polynomialDegree);
      return { type: "polynomial", equation, r2, trendData };
    }
    return null;
  }, [multiSelect, showTrendLine, showPolynomial, lastPoint, polynomialDegree]);

  const datasetsWithTrend = useMemo(() => {
    if (!datasets.length) return [];
    if (!trendInfo) return datasets;
    return [
      ...datasets,
      {
        label: trendInfo.type === "polynomial" ? "Polynomial Fit" : "Trend Line",
        data: lastPoint.time.map((t, i) => axesSwapped
          ? { x: trendInfo.trendData[i], y: new Date(t).getTime() }
          : { x: new Date(t).getTime(), y: trendInfo.trendData[i] }
        ),
        borderColor: trendInfo.type === "polynomial" ? "#00bcd4" : "#ff0000",
        backgroundColor: trendInfo.type === "polynomial" ? "#00bcd4" : "#ff0000",
        borderDash: [4, 4],
        pointRadius: 0,
        fill: false,
        showLine: true,
      },
    ];
  }, [datasets, trendInfo, axesSwapped]);

  // ── Slider track bounds (computed from visible data) ────────────────────────
  const sliderBounds = useMemo(() => {
    const allDef  = chartPoints.flatMap(pt => pt.displacement.filter(v => v != null));
    const allTime = chartPoints.flatMap(pt => pt.time.map(t => new Date(t).getTime()));
    const defPad  = (Math.max(...allDef.map(Math.abs), 1)) * 0.25;
    return {
      defMin:  allDef.length  ? Math.min(...allDef)  - defPad : -50,
      defMax:  allDef.length  ? Math.max(...allDef)  + defPad :  50,
      timeMin: allTime.length ? Math.min(...allTime)          : new Date("2020-01-01").getTime(),
      timeMax: allTime.length ? Math.max(...allTime)          : Date.now(),
    };
  }, [chartPoints]);

  const chartData = useMemo(() =>
    blurred
      ? {
          labels: ["2024-01-01", "2024-02-01", "2024-03-01"],
          datasets: [{ label: "P1", data: [0, 2, 4], borderColor: "#aaa", backgroundColor: "#aaa", pointRadius: 2, pointHoverRadius: 0, fill: false, showLine: false }],
        }
      : { datasets: datasetsWithTrend },
    [blurred, datasetsWithTrend]
  );

  const memoizedOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: !blurred,
        mode: "index",
        intersect: false,
        callbacks: {
          label: (ctx) => {
            if (!ctx.dataset) return "";
            if ((ctx.dataset.label ?? "").includes("Trend")) return "";
            const prefix = ctx.dataset.label ? ctx.dataset.label + ": " : "";
            return prefix + (ctx.parsed.y ?? "");
          },
        },
      },
    },
    hover: blurred ? { mode: null } : undefined,
    events: blurred ? [] : undefined,
    scales: axesSwapped ? {
      x: {
        type: "linear",
        border: { display: true, color: "rgba(35,34,49,0.2)", width: 1 },
        ticks: { font: { weight: "normal" }, stepSize: 5 },
        grid: { color: (ctx) => ctx.tick.value === 0 ? "rgba(35,34,49,0.45)" : "rgba(35,34,49,0.08)", lineWidth: 1 },
        title: { display: true, text: "Deformation (mm)", font: { weight: "bold" } },
        beginAtZero: true,
      },
      y: {
        type: "time",
        time: { unit: "month", tooltipFormat: "yyyy-MM-dd", displayFormats: { month: "MMM-yy" } },
        title: { display: false },
        ticks: { font: { weight: "normal" }, maxRotation: 0, minRotation: 0 },
      },
    } : {
      x: {
        type: "time",
        time: { unit: "month", tooltipFormat: "yyyy-MM-dd", displayFormats: { month: "MMM-yy" } },
        title: { display: false },
        ticks: { font: { weight: "normal" }, maxRotation: 45, minRotation: 45 },
      },
      y: {
        type: "linear",
        border: { display: true, color: "rgba(35,34,49,0.2)", width: 1 },
        ticks: { font: { weight: "normal" }, stepSize: 5 },
        grid: { color: (ctx) => ctx.tick.value === 0 ? "rgba(35,34,49,0.45)" : "rgba(35,34,49,0.08)", lineWidth: 1 },
        title: { display: true, text: "Deformation (mm)", font: { weight: "bold" } },
        beginAtZero: true,
      },
    },
  }), [blurred, axesSwapped]);

  // Key changes whenever multiSelect toggles OR dataset count changes → React fully
  // unmounts+remounts ChartCanvas, giving Chart.js a fresh canvas element every time.
  const chartKey = `${blurred}-${multiSelect}-${chartData.datasets.length}-${axesSwapped}`;

  const tableData = blurred
    ? { velocity: 12.34, vprecision: 0.56, coherence: 0.89, tDisplacement: 45.67, lat: 34.56789, lon: 32.12345 }
    : lastPoint;

  const hasEarlyWarning = allowedTools.includes("Early Warning");
  const alertUi = formatAlert(tableData?.alert);

  const hasLosSection = hasEarlyWarning && !!tableData &&
    (tableData.los_dir != null || tableData.type_motn != null || tableData.alert != null);

  return (
    <div className="sidebar-trendline">
      <div className="sidebar-trendline-title">
        <i className="fa-solid fa-chart-line"></i> Point Identifier
      </div>

      <div className="sidebar-trendline-content" style={{ padding: "16px" }}>

        {blurred ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "20px 16px", color: T.muted }}>
            <div style={{ fontSize: 22, flexShrink: 0, color: T.border }}>◎</div>
            <div style={{ fontSize: 12 }}>Click a data point on the globe to inspect.</div>
          </div>
        ) : (
          <>
            {/* ── Compact stats + chart options ── */}
            <SectionBlock first>
              {/* Stat pills */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 8px", marginBottom: 10 }}>
                {tableData.velocity != null && (
                  <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: "3px 10px", fontSize: 11 }}>
                    <span style={{ color: T.muted }}>Rate </span>
                    <span style={{ color: T.accent, fontWeight: 600 }}>{tableData.velocity.toFixed(2)} mm/yr</span>
                  </div>
                )}
                {tableData.coherence != null && (
                  <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: "3px 10px", fontSize: 11 }}>
                    <span style={{ color: T.muted }}>Coh </span>
                    <span style={{ color: T.text, fontWeight: 600 }}>{tableData.coherence.toFixed(2)}</span>
                  </div>
                )}
                {tableData.tDisplacement != null && (
                  <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: "3px 10px", fontSize: 11 }}>
                    <span style={{ color: T.muted }}>Cumul. </span>
                    <span style={{ color: T.text, fontWeight: 600 }}>{tableData.tDisplacement.toFixed(2)} mm</span>
                  </div>
                )}
                {tableData.lat != null && (
                  <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: "3px 10px", fontSize: 11, color: T.muted }}>
                    {tableData.lat.toFixed(4)}°N · {tableData.lon?.toFixed(4)}°E
                  </div>
                )}
              </div>
              {/* Inline chart options */}
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "4px 14px", fontSize: 11, color: T.muted }}>
                <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                  <input type="checkbox" checked={multiSelect} onChange={(e) => setMultiSelect(e.target.checked)} />
                  Multi-select
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                  <input type="checkbox" checked={axesSwapped} onChange={(e) => setAxesSwapped(e.target.checked)}
                    style={{ accentColor: T.accent }} />
                  Swap Axes
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                  <input type="checkbox" checked={showRangeControls} onChange={(e) => {
                    setShowRangeControls(e.target.checked);
                    if (!e.target.checked) { setDefMin(null); setDefMax(null); setDateMin(null); setDateMax(null); }
                  }} style={{ accentColor: T.accent }} />
                  Adjust Range
                </label>
                {!multiSelect && (
                  <>
                    <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                      <input type="checkbox" checked={showTrendLine} onChange={() => { setShowTrendLine(!showTrendLine); if (!showTrendLine) setShowPolynomial(false); }} />
                      Linear trend
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                      <input type="checkbox" checked={showPolynomial} onChange={() => { setShowPolynomial(!showPolynomial); if (!showPolynomial) setShowTrendLine(false); }} />
                      Polynomial
                      {showPolynomial && (
                        <select value={polynomialDegree} onChange={(e) => setPolynomialDegree(parseInt(e.target.value))}
                          style={{ fontSize: 10, padding: "1px 3px", borderRadius: 4, border: `1px solid ${T.border}`, marginLeft: 3 }}>
                          <option value={2}>x²</option><option value={3}>x³</option><option value={4}>x⁴</option>
                        </select>
                      )}
                    </label>
                  </>
                )}
              </div>
              {/* Axis range sliders */}
              {showRangeControls && !blurred && (
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                    {(defMin !== null || defMax !== null || dateMin !== null || dateMax !== null) && (
                      <button onClick={() => { setDefMin(null); setDefMax(null); setDateMin(null); setDateMax(null); }}
                        style={{ fontSize: 9, color: T.muted, background: "none", border: `1px solid ${T.border}`, borderRadius: 3, padding: "1px 6px", cursor: "pointer", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Reset
                      </button>
                    )}
                  </div>
                  <DualRangeSlider
                    label={axesSwapped ? "Deform. (X)" : "Deform. (Y)"}
                    unit=" mm"
                    trackMin={sliderBounds.defMin} trackMax={sliderBounds.defMax}
                    valueMin={defMin} valueMax={defMax}
                    onChange={(mn, mx) => { setDefMin(mn); setDefMax(mx); }}
                    fmt={v => Number(v).toFixed(1)}
                  />
                  <DualRangeSlider
                    label={axesSwapped ? "Date (Y)" : "Date (X)"}
                    trackMin={sliderBounds.timeMin} trackMax={sliderBounds.timeMax}
                    valueMin={dateMin} valueMax={dateMax}
                    onChange={(mn, mx) => { setDateMin(mn); setDateMax(mx); }}
                    fmt={v => new Date(v).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                  />
                </div>
              )}
            </SectionBlock>

            {/* ── Early Warning (only when tool is enabled) ───────────── */}
            {hasLosSection && (
              <SectionBlock>
                <SectionLabel>Early Warning</SectionLabel>
                <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: T.rSm, padding: "8px 12px" }}>
                  {tableData.los_dir   != null && <DataRow label="Line-of-sight Dir." value={formatLabelValue(tableData.los_dir)} />}
                  {tableData.type_motn != null && <DataRow label="Type of Motion"     value={formatLabelValue(tableData.type_motn)} />}
                  {tableData.alert     != null && (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", fontSize: 12, color: T.text, padding: "4px 0" }}>
                      <span style={{ color: T.muted }}>Alert Status</span>
                      <span style={{ fontWeight: 700, color: alertUi.color }}>{alertUi.text}</span>
                    </div>
                  )}
                </div>
              </SectionBlock>
            )}

            {/* ── Deformation Chart ───────────────────────────────────── */}
            <SectionBlock>
              <SectionLabel>Deformation Chart</SectionLabel>

              {/* Pills — right-aligned column below the title */}
              {(trendInfo || selectedEpoch) && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, marginTop: 6, marginBottom: 8 }}>
                  {trendInfo && (
                    <div style={{
                      fontSize: 10, color: T.muted,
                      background: T.bg, border: `1px solid ${T.border}`,
                      borderRadius: 4, padding: "2px 6px", whiteSpace: "nowrap",
                    }}>
                      <strong style={{ color: T.text }}>R²</strong> {trendInfo.r2.toFixed(3)}
                      <span style={{ margin: "0 4px", color: T.border }}>|</span>
                      {trendInfo.type === "linear"
                        ? `y = ${trendInfo.slope.toFixed(3)}x ${trendInfo.intercept < 0 ? `− ${Math.abs(trendInfo.intercept).toFixed(3)}` : `+ ${trendInfo.intercept.toFixed(3)}`}`
                        : trendInfo.equation}
                    </div>
                  )}
                  {selectedEpoch && (() => {
                    const m = selectedEpoch.match(/^D_(\d{4})(\d{2})(\d{2})$/i);
                    const label = m ? `${m[1]}-${m[2]}-${m[3]}` : selectedEpoch;
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <div style={{
                          display: "flex", alignItems: "center", gap: 5,
                          fontSize: 10, color: "#444",
                          background: "rgba(30,120,255,0.07)",
                          border: "1px solid rgba(30,120,255,0.25)",
                          borderRadius: 4, padding: "2px 6px",
                        }}>
                          <span style={{ display: "inline-block", width: 14, height: 0, borderTop: "2px dashed rgba(30,120,255,0.9)", flexShrink: 0 }} />
                          <span>Map epoch: <strong>{label}</strong></span>
                        </div>
                        {onResetEpoch && (
                          <button
                            onClick={onResetEpoch}
                            title="Reset to most recent epoch"
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "center",
                              width: 20, height: 20, padding: 0,
                              border: `1px solid ${T.border}`, borderRadius: 4,
                              background: T.bg, cursor: "pointer", color: T.muted,
                              flexShrink: 0,
                            }}
                          >
                            <RotateCcw size={11} />
                          </button>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* React-rendered legend for multiSelect mode */}
              {multiSelect && chartPoints.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 10px", marginBottom: 6 }}>
                  {chartPoints.map((pt, i) => {
                    const color = colorCache.current[`${pt.lat},${pt.lon}`] || "#000";
                    return (
                      <span key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.text }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }} />
                        {`P${i + 1}`}
                      </span>
                    );
                  })}
                </div>
              )}

              <div style={{ width: "100%", height: 200 }}>
                {!blurred && (
                  <ChartCanvas
                    key={chartKey}
                    chartData={chartData}
                    options={memoizedOptions}
                    plugins={[epochMarkerPlugin]}
                    onInstance={onChartInstance}
                  />
                )}
              </div>
            </SectionBlock>

          </>
        )}
      </div>
    </div>
  );
}
